// facilities--drawer-frame — keeps a drawer's frame showing the right thing.
//
// Two frames exist per screen, each next to an inert
// `<template id="<frame id>_pristine">` (see ./pristine_frame):
//
//   * ADD (CONEK-7405 — the same shape as every other Hotwire drawer, e.g.
//     Contacts::Companies::CreationDrawerComponent): the fresh form ships ONCE,
//     inside the template, and is cloned into the empty frame on connect, so
//     opening Add needs no request and the page carries one copy of the form's
//     option lists, not two. A controller instance on the template itself
//     reconnects when a save re-renders the drawer, so the refill happens there
//     too.
//   * EDIT: the frame starts on a skeleton of the form and the form is fetched
//     on open, or earlier by facilities--frame-prefetch.
//
// Closing a drawer by Cancel, Esc or the overlay only adds `hidden` to it, so
// on `drawer:close` (dispatched AFTER the 500ms slide-out, so the panel never
// flashes) the frame drops its `src` and goes back to its pristine content: a
// clean Add form, or the Edit skeleton, which also means a hidden fetched form
// never keeps a second set of field ids on the page (CONEK-7260).
//
// A prefetch runs while the drawer is still closed, so three Turbo frame
// events are handled here rather than left to their defaults:
//   * a response that arrives after the frame was cleared (`src` gone) is not
//     rendered — it would refill a closed drawer with a stale form;
//   * a failed fetch drops `src`, so the next click fetches again instead of
//     being treated as already loaded;
//   * a response without the frame (500, expired session → login) must not
//     navigate the whole page away on a HOVER; with the drawer open it behaves
//     as before.
//
// The instance that owns these actions sits on a wrapper around the drawer,
// because Ui::DrawerComponent does not forward arbitrary attributes to its
// root and the events bubble.
import { Controller } from "@hotwired/stimulus"

import { restorePristine } from "./pristine_frame"

export default class extends Controller {
  static values = { frameId: String }

  connect() {
    const frame = this.#frame()
    if (frame && !frame.hasChildNodes()) restorePristine(frame)
  }

  clear() {
    const frame = this.#frame()
    if (!frame) return

    // `src` would make Turbo re-fetch the stale URL on the next open.
    frame.removeAttribute("src")
    if (!restorePristine(frame)) frame.innerHTML = ""
  }

  // Turbo 8 fires turbo:before-frame-render only when a frame renders a
  // navigation response (FrameController#allowsImmediateRender). Turbo Stream
  // actions — the save, validation-error and bail-out paths — go through
  // turbo:before-stream-render instead, so this never drops one of those.
  dropStale(event) {
    const frame = this.#frame()
    if (event.target === frame && !frame.hasAttribute("src"))
      event.preventDefault()
  }

  // Turbo 8 dispatches turbo:fetch-request-error on the frame itself: a frame
  // load builds its FetchRequest with `target: this.element`.
  failed(event) {
    const frame = this.#frame()
    if (event.target !== frame) return

    frame.removeAttribute("src")
    restorePristine(frame)
  }

  missing(event) {
    const frame = this.#frame()
    if (event.target !== frame || !this.#drawerClosed(frame)) return

    event.preventDefault()
    frame.removeAttribute("src")
    restorePristine(frame)
  }

  #frame() {
    return document.getElementById(this.frameIdValue)
  }

  // Ui::DrawerComponent closes by adding `hidden` to the drawer root and has no
  // other open/closed signal the host can read, so the closest `.hidden`
  // ancestor is the drawer's own state.
  #drawerClosed(frame) {
    return frame.closest(".hidden") !== null
  }
}
